import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MiseAJourApplication } from '@stockflow/offline-core';

/**
 * « Nouvelle version disponible » — maquette « Bannière de mise à jour », écran 4.
 *
 * Montée en haut de la zone de contenu (`position: sticky`), jamais dans la barre du haut ni dans le dock : elle
 * pousse le contenu au lieu de le recouvrir, et ne touche à aucune navigation.
 *
 *  - `disponible`   : bandeau violet, « Mettre à jour » / « Plus tard ». La mise à jour recharge la page : le texte
 *                     demande d'enregistrer le travail en cours d'abord. C'est la personne qui choisit le moment.
 *  - `rechargement` : bandeau ambre, un seul bouton « Recharger » — le navigateur ne sait plus servir la version
 *                     en cache, rien d'autre ne répare.
 */
@Component({
  selector: 'app-banniere-mise-a-jour',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  template: `
    @if (maj.etat() !== 'a_jour') {
      <div class="maj-banniere" [class.maj-rechargement]="maj.etat() === 'rechargement'" role="status" aria-live="polite">
        <i class="pi pi-sync maj-icone" aria-hidden="true"></i>
        @if (maj.etat() === 'disponible') {
          <span class="maj-titre">{{ 'PWA.UPDATE_TITLE' | translate }}</span>
          <span class="maj-texte">{{ 'PWA.UPDATE_BODY' | translate }}</span>
          <button type="button" class="maj-action" (click)="appliquer()">{{ 'PWA.UPDATE_ACTION' | translate }}</button>
          <button type="button" class="maj-secondaire" (click)="maj.ecarter()">{{ 'PWA.UPDATE_LATER' | translate }}</button>
        } @else {
          <span class="maj-titre">{{ 'PWA.RELOAD_TITLE' | translate }}</span>
          <span class="maj-texte">{{ 'PWA.RELOAD_BODY' | translate }}</span>
          <button type="button" class="maj-action" (click)="maj.recharger()">{{ 'PWA.RELOAD_ACTION' | translate }}</button>
        }
      </div>
    }
  `,
  styles: [`
    .maj-banniere {
      position: sticky;
      top: 0;
      z-index: 900;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.5rem 0.875rem;
      min-height: 52px;
      padding: 0.5rem 1.5rem;
      color: #fff;
      background: linear-gradient(135deg, #5b52c4 0%, #7f77dd 60%, #9b6bc4 100%);
    }
    .maj-rechargement { background: linear-gradient(135deg, #92400e 0%, #b45309 100%); }
    .maj-icone { flex: none; font-size: 1.125rem; }
    .maj-titre { font-size: 0.875rem; font-weight: 700; }
    .maj-texte { flex: 1; min-width: 200px; font-size: 0.8125rem; opacity: 0.92; }
    .maj-action, .maj-secondaire {
      min-height: 44px;
      padding: 0 1.125rem;
      border-radius: 9px;
      font: inherit;
      font-size: 0.8125rem;
      cursor: pointer;
    }
    .maj-action { border: 0; background: #fff; color: #4a3fb3; font-weight: 700; }
    .maj-rechargement .maj-action { color: #92400e; }
    .maj-secondaire { border: 1px solid rgba(255, 255, 255, 0.45); background: transparent; color: #fff; font-weight: 600; }
    .maj-action:focus-visible, .maj-secondaire:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
  `],
})
export class BanniereMiseAJourComponent {
  protected readonly maj = inject(MiseAJourApplication);

  async appliquer(): Promise<void> {
    await this.maj.appliquer();
  }
}
